import React, { useEffect, useMemo, useState } from 'react';
import { publicationsAPI } from '../../services/api';
import { useUser } from '../../context/UserContext';
import './PublicationsManagement.css';

const MAX_PUBLICATION_FILE_SIZE = 250 * 1024 * 1024;
const MAX_PUBLICATION_FILE_SIZE_LABEL = '250 MB';
const PAGE_FILE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'webp', 'gif']);

const emptyPublication = {
  title: '',
  slug: '',
  subtitle: '',
  description: '',
  issueLabel: '',
  categoryId: '',
  visibility: 'draft',
  isFlipbook: true,
  gapless: false,
  allowDownload: true,
  active: true,
  displayOrder: 0,
  publishedAt: '',
  pdf: null,
  pages: [],
  cover: null
};

const emptyCategory = {
  name: '',
  slug: '',
  description: '',
  displayOrder: 0,
  active: true
};

const toDateTimeLocal = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60 * 1000).toISOString().slice(0, 16);
};

const formatBytes = (bytes) => {
  const value = Number(bytes || 0);
  if (value < 1024 * 1024) return `${Math.max(1, Math.round(value / 1024))} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
};

const PublicationsManagement = () => {
  const { hasPermission } = useUser();
  const canCreate = hasPermission('publications.create');
  const canEdit = hasPermission('publications.edit');
  const canDelete = hasPermission('publications.delete');
  const canControl = hasPermission('publications.control');
  const [view, setView] = useState('publications');
  const [publications, setPublications] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [publicationFormOpen, setPublicationFormOpen] = useState(false);
  const [editingPublication, setEditingPublication] = useState(null);
  const [publicationForm, setPublicationForm] = useState(emptyPublication);
  const [publicationSubmitting, setPublicationSubmitting] = useState(false);
  const [categoryFormOpen, setCategoryFormOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState(emptyCategory);
  const [categorySubmitting, setCategorySubmitting] = useState(false);

  const activeCategories = useMemo(
    () => categories.filter((category) => category.active || category.id === Number(publicationForm.categoryId)),
    [categories, publicationForm.categoryId]
  );

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const [publicationResponse, categoryResponse] = await Promise.all([
        publicationsAPI.getAll(),
        publicationsAPI.getAdminCategories()
      ]);
      setPublications(publicationResponse.data || []);
      setCategories(categoryResponse.data || []);
    } catch (loadError) {
      console.error('Failed to load publications:', loadError);
      setError(loadError.message || 'Failed to load publications.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const flash = (message) => {
    setNotice(message);
    window.setTimeout(() => setNotice(''), 3500);
  };

  const openNewPublication = () => {
    setEditingPublication(null);
    setPublicationForm({
      ...emptyPublication,
      categoryId: categories.find((category) => category.active)?.id || ''
    });
    setPublicationFormOpen(true);
  };

  const openEditPublication = (publication) => {
    setEditingPublication(publication);
    setPublicationForm({
      title: publication.title || '',
      slug: publication.slug || '',
      subtitle: publication.subtitle || '',
      description: publication.description || '',
      issueLabel: publication.issue_label || '',
      categoryId: publication.category_id || '',
      visibility: publication.visibility || 'draft',
      isFlipbook: publication.is_flipbook !== false,
      gapless: Boolean(publication.gapless),
      allowDownload: Boolean(publication.allow_download),
      active: Boolean(publication.active),
      displayOrder: publication.display_order || 0,
      publishedAt: toDateTimeLocal(publication.published_at),
      pdf: null,
      pages: [],
      cover: null
    });
    setPublicationFormOpen(true);
  };

  const closePublicationForm = () => {
    setPublicationFormOpen(false);
    setEditingPublication(null);
    setPublicationForm(emptyPublication);
  };

  const buildPublicationFormData = () => {
    const formData = new FormData();
    Object.entries(publicationForm).forEach(([key, value]) => {
      if (key === 'pages') {
        value.forEach((file) => formData.append('pages', file));
      } else if ((key === 'pdf' || key === 'cover') && value) {
        formData.append(key, value);
      } else if (key !== 'pdf' && key !== 'cover') {
        formData.append(key, value === null ? '' : String(value));
      }
    });
    return formData;
  };

  const selectPublicationFiles = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (files.length === 0) return;

    const pdfFiles = files.filter((file) => (
      file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
    ));
    if (pdfFiles.length > 0 && files.length !== 1) {
      setError('Choose one PDF, or choose individual image pages without mixing the two formats.');
      return;
    }

    const invalidPage = files.find((file) => {
      const extension = file.name.split('.').pop()?.toLowerCase();
      return pdfFiles.length === 0 && !PAGE_FILE_EXTENSIONS.has(extension);
    });
    if (invalidPage) {
      setError('Page files must be JPEG, PNG, WebP, or GIF images.');
      return;
    }

    const selectedSize = files.reduce((sum, file) => sum + file.size, 0);
    const existingPageSize = pdfFiles.length === 0
      ? publicationForm.pages.reduce((sum, file) => sum + file.size, 0)
      : 0;
    if (selectedSize + existingPageSize > MAX_PUBLICATION_FILE_SIZE) {
      event.target.value = '';
      setError(`Publication content must be ${MAX_PUBLICATION_FILE_SIZE_LABEL} or smaller in total.`);
      return;
    }

    setError('');
    setPublicationForm((current) => pdfFiles.length === 1
      ? { ...current, pdf: pdfFiles[0], pages: [] }
      : { ...current, pdf: null, pages: [...current.pages, ...files] });
  };

  const movePublicationPage = (index, direction) => {
    setPublicationForm((current) => {
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= current.pages.length) return current;
      const pages = [...current.pages];
      [pages[index], pages[targetIndex]] = [pages[targetIndex], pages[index]];
      return { ...current, pages };
    });
  };

  const removePublicationPage = (index) => {
    setPublicationForm((current) => ({
      ...current,
      pages: current.pages.filter((file, fileIndex) => fileIndex !== index)
    }));
  };

  const savePublication = async (event) => {
    event.preventDefault();
    if (!publicationForm.title.trim()) {
      setError('Publication title is required.');
      return;
    }
    if (!editingPublication && !publicationForm.pdf && publicationForm.pages.length === 0) {
      setError('Choose a PDF or add individual page files for the new publication.');
      return;
    }
    const publicationContentSize = publicationForm.pdf?.size || publicationForm.pages
      .reduce((sum, file) => sum + file.size, 0);
    if (publicationContentSize > MAX_PUBLICATION_FILE_SIZE) {
      setError(`Publication content must be ${MAX_PUBLICATION_FILE_SIZE_LABEL} or smaller in total.`);
      return;
    }

    try {
      setPublicationSubmitting(true);
      setError('');
      const response = editingPublication
        ? await publicationsAPI.update(editingPublication.id, buildPublicationFormData())
        : await publicationsAPI.create(buildPublicationFormData());
      closePublicationForm();
      await loadData();
      flash(response.message || 'Publication saved.');
    } catch (saveError) {
      console.error('Failed to save publication:', saveError);
      setError(saveError.message || 'Failed to save publication.');
    } finally {
      setPublicationSubmitting(false);
    }
  };

  const togglePublicationArchive = async (publication) => {
    const formData = new FormData();
    formData.append('active', String(!publication.active));
    try {
      setError('');
      const response = await publicationsAPI.update(publication.id, formData);
      await loadData();
      flash(response.message || 'Publication updated.');
    } catch (updateError) {
      setError(updateError.message || 'Failed to update publication.');
    }
  };

  const deletePublication = async (publication) => {
    if (!window.confirm(`Permanently delete "${publication.title}" and its uploaded files?`)) return;
    try {
      setError('');
      await publicationsAPI.delete(publication.id);
      await loadData();
      flash('Publication deleted.');
    } catch (deleteError) {
      setError(deleteError.message || 'Failed to delete publication.');
    }
  };

  const copyShareLink = async (publication) => {
    const link = `${window.location.origin}${publication.share_url}`;
    try {
      await navigator.clipboard.writeText(link);
      flash('Share link copied.');
    } catch (copyError) {
      window.prompt('Copy this publication link:', link);
    }
  };

  const openNewCategory = () => {
    setEditingCategory(null);
    setCategoryForm(emptyCategory);
    setCategoryFormOpen(true);
  };

  const openEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name || '',
      slug: category.slug || '',
      description: category.description || '',
      displayOrder: category.display_order || 0,
      active: Boolean(category.active)
    });
    setCategoryFormOpen(true);
  };

  const closeCategoryForm = () => {
    setCategoryFormOpen(false);
    setEditingCategory(null);
    setCategoryForm(emptyCategory);
  };

  const saveCategory = async (event) => {
    event.preventDefault();
    if (!categoryForm.name.trim()) {
      setError('Category name is required.');
      return;
    }
    try {
      setCategorySubmitting(true);
      setError('');
      const response = editingCategory
        ? await publicationsAPI.updateCategory(editingCategory.id, categoryForm)
        : await publicationsAPI.createCategory(categoryForm);
      closeCategoryForm();
      await loadData();
      flash(response.message || 'Category saved.');
    } catch (saveError) {
      setError(saveError.message || 'Failed to save category.');
    } finally {
      setCategorySubmitting(false);
    }
  };

  const deleteCategory = async (category) => {
    if (!window.confirm(`Permanently delete the "${category.name}" category?`)) return;
    try {
      setError('');
      await publicationsAPI.deleteCategory(category.id);
      await loadData();
      flash('Category deleted.');
    } catch (deleteError) {
      setError(deleteError.message || 'Failed to delete category.');
    }
  };

  if (loading) {
    return (
      <div className="admin-section publications-admin">
        <div className="loading-message">Loading publications...</div>
      </div>
    );
  }

  return (
    <div className="admin-section publications-admin">
      <div className="admin-section-header publications-admin-header">
        <div>
          <h2 className="admin-section-title">Publications</h2>
          <p className="publications-admin-intro">
            Manage self-hosted magazines, articles, releases, access, and share links.
          </p>
        </div>
        {((view === 'publications' && canCreate) || (view === 'categories' && canControl)) && (
          <div className="admin-section-actions">
            <button
              className="admin-button primary"
              type="button"
              onClick={view === 'publications' ? openNewPublication : openNewCategory}
            >
              {view === 'publications' ? '+ Add Publication' : '+ Add Category'}
            </button>
          </div>
        )}
      </div>

      <div className="publications-admin-tabs" role="tablist" aria-label="Publication management">
        <button
          type="button"
          className={view === 'publications' ? 'active' : ''}
          onClick={() => setView('publications')}
        >
          Releases
        </button>
        {canControl && (
          <button
            type="button"
            className={view === 'categories' ? 'active' : ''}
            onClick={() => setView('categories')}
          >
            Categories
          </button>
        )}
      </div>

      {notice && <div className="publications-admin-message success">{notice}</div>}
      {error && <div className="publications-admin-message error">{error}</div>}

      {view === 'publications' && publicationFormOpen && (
        <form className="publications-admin-form" onSubmit={savePublication}>
          <div className="publications-form-heading">
            <div>
              <span className="publications-form-kicker">Release details</span>
              <h3>{editingPublication ? 'Edit Publication' : 'New Publication'}</h3>
            </div>
            <button
              type="button"
              className="publications-icon-button"
              onClick={closePublicationForm}
              aria-label="Close publication form"
              title="Close"
            >
              ×
            </button>
          </div>

          <div className="publications-form-grid">
            <label className="admin-form-group">
              <span className="admin-form-label">Title</span>
              <input
                className="admin-form-input"
                value={publicationForm.title}
                onChange={(event) => setPublicationForm((current) => ({ ...current, title: event.target.value }))}
                required
              />
            </label>
            <label className="admin-form-group">
              <span className="admin-form-label">Share URL slug</span>
              <input
                className="admin-form-input"
                value={publicationForm.slug}
                onChange={(event) => setPublicationForm((current) => ({ ...current, slug: event.target.value }))}
                placeholder="generated-from-title"
              />
            </label>
            <label className="admin-form-group">
              <span className="admin-form-label">Subtitle</span>
              <input
                className="admin-form-input"
                value={publicationForm.subtitle}
                onChange={(event) => setPublicationForm((current) => ({ ...current, subtitle: event.target.value }))}
              />
            </label>
            <label className="admin-form-group">
              <span className="admin-form-label">Issue label</span>
              <input
                className="admin-form-input"
                value={publicationForm.issueLabel}
                onChange={(event) => setPublicationForm((current) => ({ ...current, issueLabel: event.target.value }))}
                placeholder="Volume 1 · Summer 2026"
              />
            </label>
            <label className="admin-form-group">
              <span className="admin-form-label">Category</span>
              <select
                className="admin-form-select"
                value={publicationForm.categoryId}
                onChange={(event) => setPublicationForm((current) => ({ ...current, categoryId: event.target.value }))}
              >
                <option value="">Uncategorised</option>
                {activeCategories.map((category) => (
                  <option key={category.id} value={category.id}>{category.name}</option>
                ))}
              </select>
            </label>
            <label className="admin-form-group">
              <span className="admin-form-label">Access</span>
              <select
                className="admin-form-select"
                value={publicationForm.visibility}
                onChange={(event) => setPublicationForm((current) => ({ ...current, visibility: event.target.value }))}
              >
                <option value="draft">Draft · admins only</option>
                <option value="public">Public · shown in library</option>
                <option value="unlisted">Unlisted · link only</option>
                <option value="staff">Staff · signed-in staff only</option>
              </select>
            </label>
            <label className="admin-form-group">
              <span className="admin-form-label">Publish date</span>
              <input
                className="admin-form-input"
                type="datetime-local"
                value={publicationForm.publishedAt}
                onChange={(event) => setPublicationForm((current) => ({ ...current, publishedAt: event.target.value }))}
              />
            </label>
            <label className="admin-form-group">
              <span className="admin-form-label">Display order</span>
              <input
                className="admin-form-input"
                type="number"
                value={publicationForm.displayOrder}
                onChange={(event) => setPublicationForm((current) => ({ ...current, displayOrder: event.target.value }))}
              />
            </label>
            <label className="admin-form-group publications-form-wide">
              <span className="admin-form-label">Short description</span>
              <textarea
                className="admin-form-textarea"
                rows="4"
                value={publicationForm.description}
                onChange={(event) => setPublicationForm((current) => ({ ...current, description: event.target.value }))}
              />
            </label>
            <div className="admin-form-group">
              <span className="admin-form-label">
                Publication content {editingPublication ? '(leave empty to keep current)' : ''}
              </span>
              <input
                className="admin-form-input publications-file-input"
                type="file"
                accept="application/pdf,image/jpeg,image/png,image/webp,image/gif,.pdf,.jpg,.jpeg,.png,.webp,.gif"
                multiple
                onChange={selectPublicationFiles}
              />
              <small className="admin-form-hint">
                Select one PDF, or add image and GIF pages in display order. Maximum total: {MAX_PUBLICATION_FILE_SIZE_LABEL}.
              </small>
              {editingPublication && (
                <small className="admin-form-hint">
                  Current: {editingPublication.file_type === 'pages'
                    ? `${editingPublication.page_count} ordered files`
                    : editingPublication.pdf_original_name} · {formatBytes(editingPublication.file_size)}
                </small>
              )}
              {publicationForm.pdf && (
                <div className="publication-selected-pdf">
                  <span>{publicationForm.pdf.name}</span>
                  <button
                    type="button"
                    onClick={() => setPublicationForm((current) => ({ ...current, pdf: null }))}
                    aria-label="Remove selected PDF"
                    title="Remove selected PDF"
                  >
                    ×
                  </button>
                </div>
              )}
              {publicationForm.pages.length > 0 && (
                <ol className="publication-page-upload-list">
                  {publicationForm.pages.map((file, index) => (
                    <li key={`${file.name}-${file.lastModified}-${index}`}>
                      <span className="publication-page-order">{index + 1}</span>
                      <span className="publication-page-name">{file.name}</span>
                      <span className="publication-page-size">{formatBytes(file.size)}</span>
                      <button
                        type="button"
                        onClick={() => movePublicationPage(index, -1)}
                        disabled={index === 0}
                        aria-label={`Move ${file.name} up`}
                        title="Move up"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        onClick={() => movePublicationPage(index, 1)}
                        disabled={index === publicationForm.pages.length - 1}
                        aria-label={`Move ${file.name} down`}
                        title="Move down"
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        className="remove"
                        onClick={() => removePublicationPage(index)}
                        aria-label={`Remove ${file.name}`}
                        title="Remove page"
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ol>
              )}
            </div>
            <label className="admin-form-group">
              <span className="admin-form-label">
                Cover image {editingPublication ? '(leave empty to keep current)' : ''}
              </span>
              <input
                className="admin-form-input publications-file-input"
                type="file"
                accept="image/*"
                onChange={(event) => setPublicationForm((current) => ({
                  ...current,
                  cover: event.target.files?.[0] || null
                }))}
              />
              <small className="admin-form-hint">Converted to an optimized WebP automatically.</small>
            </label>
          </div>

          <div className="publications-form-toggles">
            <label className="admin-checkbox-label">
              <input
                type="checkbox"
                checked={publicationForm.isFlipbook}
                onChange={(event) => setPublicationForm((current) => ({
                  ...current,
                  isFlipbook: event.target.checked
                }))}
              />
              This Publication is a Flipbook
            </label>
            {!publicationForm.isFlipbook && (
              <label className="admin-checkbox-label">
                <input
                  type="checkbox"
                  checked={publicationForm.gapless}
                  onChange={(event) => setPublicationForm((current) => ({
                    ...current,
                    gapless: event.target.checked
                  }))}
                />
                Gapless
              </label>
            )}
            <label className="admin-checkbox-label">
              <input
                type="checkbox"
                checked={publicationForm.allowDownload}
                onChange={(event) => setPublicationForm((current) => ({
                  ...current,
                  allowDownload: event.target.checked
                }))}
              />
              Allow publication download
            </label>
            <label className="admin-checkbox-label">
              <input
                type="checkbox"
                checked={publicationForm.active}
                onChange={(event) => setPublicationForm((current) => ({
                  ...current,
                  active: event.target.checked
                }))}
              />
              Active
            </label>
          </div>

          <div className="admin-form-actions">
            <button className="admin-button primary" type="submit" disabled={publicationSubmitting}>
              {publicationSubmitting ? 'Uploading...' : editingPublication ? 'Save Changes' : 'Create Publication'}
            </button>
            <button className="admin-button secondary" type="button" onClick={closePublicationForm}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {view === 'publications' && (
        publications.length === 0 ? (
          <div className="admin-empty-state">
            <p>No publications yet. Upload a PDF or ordered page files.</p>
          </div>
        ) : (
          <div className="admin-table-wrapper">
            <table className="admin-table publications-table">
              <thead>
                <tr>
                  <th>Publication</th>
                  <th>Category</th>
                  <th>Format</th>
                  <th>Access</th>
                  <th>File</th>
                  <th>Views</th>
                  <th>Order</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {publications.map((publication) => (
                  <tr key={publication.id}>
                    <td>
                      <div className="publication-admin-title-cell">
                        {publication.cover_image_url ? (
                          <img src={publication.cover_image_url} alt="" />
                        ) : (
                          <span className="publication-admin-cover-fallback">V</span>
                        )}
                        <div>
                          <strong>{publication.title}</strong>
                          <span>{publication.issue_label || publication.slug}</span>
                        </div>
                      </div>
                    </td>
                    <td>{publication.category_name || 'Uncategorised'}</td>
                    <td>
                      <span className={`publication-format-badge ${publication.is_flipbook ? 'flipbook' : 'scrolling'}`}>
                        {publication.file_type === 'pages'
                          ? publication.is_flipbook
                            ? 'Page Flipbook'
                            : publication.gapless ? 'Gapless Pages' : 'Scrolling Pages'
                          : publication.is_flipbook ? 'Flipbook' : publication.gapless ? 'Gapless PDF' : 'Scrolling PDF'}
                      </span>
                    </td>
                    <td>
                      <span className={`publication-access-badge ${publication.visibility}`}>
                        {publication.visibility}
                      </span>
                    </td>
                    <td>
                      {publication.page_count
                        ? `${publication.page_count} ${publication.file_type === 'pages' ? 'files' : 'pages'} · `
                        : ''}
                      {formatBytes(publication.file_size)}
                    </td>
                    <td className="publication-view-count">
                      {Number(publication.view_count || 0).toLocaleString()}
                    </td>
                    <td>{publication.display_order}</td>
                    <td>
                      <span className={`admin-product-status-badge ${publication.active ? 'positive' : 'neutral'}`}>
                        {publication.active ? 'Active' : 'Archived'}
                      </span>
                    </td>
                    <td>
                      <div className="admin-table-actions publications-row-actions">
                        <button className="admin-button secondary small" type="button" onClick={() => copyShareLink(publication)}>
                          Copy Link
                        </button>
                        {canEdit && (
                          <button className="admin-button secondary small" type="button" onClick={() => openEditPublication(publication)}>
                            Edit
                          </button>
                        )}
                        {canEdit && (
                          <button className="admin-button secondary small" type="button" onClick={() => togglePublicationArchive(publication)}>
                            {publication.active ? 'Archive' : 'Restore'}
                          </button>
                        )}
                        {canDelete && (
                          <button className="admin-button danger small" type="button" onClick={() => deletePublication(publication)}>
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {view === 'categories' && categoryFormOpen && (
        <form className="publications-admin-form publications-category-form" onSubmit={saveCategory}>
          <div className="publications-form-heading">
            <div>
              <span className="publications-form-kicker">Library organisation</span>
              <h3>{editingCategory ? 'Edit Category' : 'New Category'}</h3>
            </div>
            <button
              type="button"
              className="publications-icon-button"
              onClick={closeCategoryForm}
              aria-label="Close category form"
              title="Close"
            >
              ×
            </button>
          </div>
          <div className="publications-form-grid">
            <label className="admin-form-group">
              <span className="admin-form-label">Name</span>
              <input
                className="admin-form-input"
                value={categoryForm.name}
                onChange={(event) => setCategoryForm((current) => ({ ...current, name: event.target.value }))}
                required
              />
            </label>
            <label className="admin-form-group">
              <span className="admin-form-label">Slug</span>
              <input
                className="admin-form-input"
                value={categoryForm.slug}
                onChange={(event) => setCategoryForm((current) => ({ ...current, slug: event.target.value }))}
                placeholder="generated-from-name"
              />
            </label>
            <label className="admin-form-group">
              <span className="admin-form-label">Display order</span>
              <input
                className="admin-form-input"
                type="number"
                value={categoryForm.displayOrder}
                onChange={(event) => setCategoryForm((current) => ({ ...current, displayOrder: event.target.value }))}
              />
            </label>
            <label className="admin-form-group publications-form-wide">
              <span className="admin-form-label">Description</span>
              <textarea
                className="admin-form-textarea"
                rows="3"
                value={categoryForm.description}
                onChange={(event) => setCategoryForm((current) => ({ ...current, description: event.target.value }))}
              />
            </label>
          </div>
          <label className="admin-checkbox-label">
            <input
              type="checkbox"
              checked={categoryForm.active}
              onChange={(event) => setCategoryForm((current) => ({ ...current, active: event.target.checked }))}
            />
            Active
          </label>
          <div className="admin-form-actions">
            <button className="admin-button primary" type="submit" disabled={categorySubmitting}>
              {categorySubmitting ? 'Saving...' : 'Save Category'}
            </button>
            <button className="admin-button secondary" type="button" onClick={closeCategoryForm}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {view === 'categories' && (
        categories.length === 0 ? (
          <div className="admin-empty-state">
            <p>No categories yet.</p>
          </div>
        ) : (
          <div className="admin-table-wrapper">
            <table className="admin-table publications-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Slug</th>
                  <th>Publications</th>
                  <th>Order</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((category) => (
                  <tr key={category.id}>
                    <td>
                      <strong>{category.name}</strong>
                      {category.description && <div className="admin-description">{category.description}</div>}
                    </td>
                    <td>{category.slug}</td>
                    <td>{category.publication_count}</td>
                    <td>{category.display_order}</td>
                    <td>
                      <span className={`admin-product-status-badge ${category.active ? 'positive' : 'neutral'}`}>
                        {category.active ? 'Active' : 'Archived'}
                      </span>
                    </td>
                    <td>
                      <div className="admin-table-actions">
                        {canControl && (
                          <button className="admin-button secondary small" type="button" onClick={() => openEditCategory(category)}>
                            Edit
                          </button>
                        )}
                        {canControl && (
                          <button className="admin-button danger small" type="button" onClick={() => deleteCategory(category)}>
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
};

export default PublicationsManagement;
